import CircularProgress from "@mui/material/CircularProgress";
import React, { ComponentType } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useParams } from "react-router";
import { usePayerLandscapeDetailDropdowns } from "../api/usePayerLandscapeDetailApi";
import { useClientPrintConfiguration, useClientRouteOverrides } from "../data/global/useProductConfiguration";
import useDownloadZippedDocs from "../hooks/useDownloadZippedDocs";
import useGetRole, { BIOSIM_ROLE, FAS_ROLE, FRM_ROLE, GENERAL_ROLE } from "../hooks/useGetRole";
import useHasHcpAccessAsFrm from "../hooks/useHasHcpAccessAsFrm";
import DefaultLayout from "../layouts/DefaultLayout";
import Email from "../pages/Email/Email";
import GeoLandscape from "../pages/GeoLandscape/GeoLandscape";
import GeoProfile from "../pages/GeoProfile/GeoProfile";
import HCPLandscape from "../pages/HCPLandscape/HCPLandscape";
import HCPProfile from "../pages/HCPProfile/HCPProfile";
import HowTo from "../pages/HowTo/HowTo";
import ImplementationGuide from "../pages/ImplementationGuide/ImplementationGuide";
import Messages from "../pages/Messages/Messages";
import PayerLandscape from "../pages/PayerLandscape/PayerLandscape";
import HealthcareProviders from "../pages/PlanProfile/HealthcareProviders";
import PlanProfile from "../pages/PlanProfile/PlanProfile";
import Queue from "../pages/Queue/Queue";
import Resources from "../pages/Resources/Resources";
import PageNotFound from "../pages/SystemError/PageNotFound";
import UserProfile from "../pages/UserProfile/UserProfile";
import { useClientRoutesAndLabels } from "./routes";

type WithRole = {
  allowedRoles?: string[];
};

const withRole =
  <P extends Record<string, unknown>>(
    Component: ComponentType<P>,
    options: WithRole = { allowedRoles: [FRM_ROLE, GENERAL_ROLE, FAS_ROLE, BIOSIM_ROLE] },
  ): React.FC<P> =>
  (props: P): React.JSX.Element => {
    const { allowedRoles } = options;
    const { client: selectedClient } = useParams<{ client: string }>();
    if (!selectedClient) {
      throw new Error("Client is required but was undefined.");
    }
    const routes = useClientRoutesAndLabels(selectedClient);

    const { data: role, isIdle, isFetched, isFetching } = useGetRole(selectedClient);
    const hasRole = role && allowedRoles?.includes(String(role));

    if ((isIdle && !isFetched) || isFetching) {
      return (
        <div className="flex h-screen justify-center items-center">
          <CircularProgress size={80} />
        </div>
      );
    }

    if (!hasRole) {
      return <Navigate to={routes.client.dpp.with({ client: selectedClient })} />;
    }

    return <Component {...props} />;
  };

const withGeneralUser = <P extends Record<string, unknown>>(Component: ComponentType<P>): React.FC<P> =>
  withRole(Component, {
    allowedRoles: [GENERAL_ROLE, FAS_ROLE, BIOSIM_ROLE],
  });

const withGeneralAndHcpFrmUser = <P extends Record<string, unknown>>(
  Component: ComponentType<P>,
  hasHcpAccessAsFrm?: boolean,
): React.FC<P> => {
  return hasHcpAccessAsFrm
    ? withRole(Component, {
        allowedRoles: [GENERAL_ROLE, FAS_ROLE, FRM_ROLE, BIOSIM_ROLE],
      })
    : withRole(Component, {
        allowedRoles: [GENERAL_ROLE, FAS_ROLE, BIOSIM_ROLE],
      });
};

const withGeneralAndPrintFrmUser = <P extends Record<string, unknown>>(
  Component: ComponentType<P>,
  hasPrintAccessAsFrm?: boolean,
): React.FC<P> => {
  return hasPrintAccessAsFrm
    ? withRole(Component, {
        allowedRoles: [GENERAL_ROLE, FAS_ROLE, FRM_ROLE, BIOSIM_ROLE],
      })
    : withRole(Component, {
        allowedRoles: [GENERAL_ROLE, FAS_ROLE, BIOSIM_ROLE],
      });
};

const withPayerLandscapeDetailDropdowns =
  <P extends Record<string, unknown>>(Component: ComponentType<P>): React.FC<P> =>
  (props: P): React.JSX.Element => {
    const { client: selectedClient } = useParams<{ client: string }>();
    if (!selectedClient) {
      throw new Error("Client is required but was undefined.");
    }
    const { isIdle, isFetched, isFetching } = usePayerLandscapeDetailDropdowns(selectedClient);

    if ((isIdle && !isFetched) || isFetching) {
      return (
        <div className="flex h-screen justify-center items-center">
          <CircularProgress size={80} />
        </div>
      );
    }

    return <Component {...props} />;
  };

const DefaultLayoutRouter: React.FC = () => {
  const [t, i18n] = useTranslation();
  const { client: selectedClient } = useParams<{ client: string }>();
  if (!selectedClient) {
    throw new Error("Client is required but was undefined.");
  }

  useDownloadZippedDocs(selectedClient);
  const printConfig = useClientPrintConfiguration(selectedClient);

  const routeOverrides = useClientRouteOverrides(selectedClient);
  const hcpsPath =
    typeof routeOverrides?.client === "object" &&
    typeof routeOverrides.client.dpp === "object" &&
    typeof routeOverrides.client.dpp.hcps === "object" &&
    "path" in routeOverrides.client.dpp.hcps
      ? routeOverrides.client.dpp.hcps.path
      : "hcps";

  const { data: role, isFetched } = useGetRole(selectedClient);
  const isFRM = !isFetched || String(role) === FRM_ROLE;
  const hasHcpAccessAsFrm = useHasHcpAccessAsFrm(selectedClient, isFRM);

  return (
    <>
      <Helmet>
        <title>
          {i18n.exists(`defaultLayout.appNames.${selectedClient}`)
            ? t(`defaultLayout.appNames.${selectedClient}`)
            : t("defaultLayout.appName", { product: selectedClient?.split("_")?.join(" ") })}
        </title>
        <meta
          name="description"
          content={
            (i18n.exists(`defaultLayout.appNames.${selectedClient}`)
              ? t(`defaultLayout.appNames.${selectedClient}`)
              : t("defaultLayout.appName", { product: selectedClient?.split("_")?.join(" ") })) as string
          }
        />
      </Helmet>
      <DefaultLayout>
        <Routes>
          {/* :client/dpp/ */}
          <Route path={"dpp"}>
            {/* index definition for :client/dpp/ */}
            <Route index element={<Navigate to={"payers"} />} />

            {/* :client/dpp/payers routes */}
            <Route path={"payers"}>
              {/* Default route :client/dpp/payers */}
              <Route index element={React.createElement(withRole(withPayerLandscapeDetailDropdowns(PayerLandscape)))} />

              {/* :client/dpp/payer/:id */}
              <Route path={":id"}>
                {/* Root route :client/dpp/payer/:id */}
                <Route index element={React.createElement(withGeneralUser(PlanProfile))} />

                {/* :client/dpp/payer/:id/providers */}
                <Route path={"providers"} element={React.createElement(withGeneralUser(HealthcareProviders))} />

                {/* :client/dpp/payer/:id/:hcpID */}
                <Route path={":hcpID"}>
                  {/* Default route :client/dpp/payer/:id/:hcpID */}
                  <Route index element={React.createElement(withGeneralUser(PlanProfile))} />
                  {/* :client/dpp/payer/:id/:hcpID/providers */}
                  <Route path={"providers"} element={React.createElement(withGeneralUser(HealthcareProviders))} />
                </Route>
              </Route>
            </Route>

            {/* :client/dpp/hcps routes */}
            <Route path={hcpsPath}>
              <Route index element={React.createElement(withGeneralAndHcpFrmUser(HCPLandscape, hasHcpAccessAsFrm))} />
              {/* :client/dpp/hcps/:id */}
              <Route path={":id"} element={React.createElement(withGeneralUser(HCPProfile))} />
            </Route>

            {/* :client/dpp/geo routes */}
            <Route path={"geo"}>
              {/* Default route :client/dpp/geo */}
              <Route index element={React.createElement(withGeneralUser(GeoLandscape))} />

              {/* :client/dpp/geo/:state   */}
              <Route path={":state"} element={React.createElement(withGeneralUser(GeoProfile))} />
            </Route>

            <Route path="*" element={<PageNotFound />} />
          </Route>

          {/* :client/print/ */}
          <Route path={"print"}>
            {/* index definition for :client/print/ */}
            <Route index element={<Navigate to={printConfig.hcpPrintMessages ? "messages" : "resources"} replace />} />
            {/* :client/print/messages */}
            <Route
              path={"messages"}
              element={React.createElement(withGeneralAndPrintFrmUser(Messages, printConfig.printEnabledForFRM))}
            />
            {/* :client/print/resources */}
            <Route
              path={"resources"}
              element={React.createElement(withGeneralAndPrintFrmUser(Resources, printConfig.printEnabledForFRM))}
            />
            {/* :client/print/queue */}
            <Route
              path={"queue"}
              element={
                printConfig.disablePrintQueue ? (
                  <PageNotFound />
                ) : (
                  React.createElement(withGeneralAndPrintFrmUser(Queue, printConfig.printEnabledForFRM))
                )
              }
            />
            {/* :client/print/email */}
            <Route
              path={"email"}
              element={React.createElement(withGeneralAndPrintFrmUser(Email, printConfig.printEnabledForFRM))}
            />
            <Route path="*" element={<PageNotFound />} />
          </Route>

          {/* :client/profile */}
          <Route path={"profile"} element={React.createElement(withRole(UserProfile))} />

          {/* :client/how-to */}
          <Route path={"how-to"} element={<HowTo />} />

          {/* :client/implementation-guide */}
          <Route path={"implementation-guide"} element={<ImplementationGuide />} />

          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </DefaultLayout>
    </>
  );
};

export default DefaultLayoutRouter;
